import { useAlertContext } from '@dealstack/frontend/shared/components/Alert/AlertsContext';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { IconButton } from '@dealstack/frontend/shared/components/fundament/IconButton/IconButton';
import { Text, typographyCss } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { rem } from '@mantine/core';
import { forwardRef, PropsWithChildren, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { PredefinedTextVariants } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import type { IAlertProps } from './Alert.types';

interface IAlertPropsBySize {
	typographyVariants: {
		title: PredefinedTextVariants;
		description: PredefinedTextVariants;
		buttonText: PredefinedTextVariants;
	};
	alertIcon: string;
	iconWrapper: {
		marginLeft: string;
		marginBottom: string;
	};
	alertButton: {
		iconSize: string;
		marginRight: string;
		marginTop: string;
	};
	contentMargins: {
		childrenWrapperMarginLeft: string;
		titleMarginBottom: string;
	};
	padding: string;
}

const AlertWrapper = styled.div`
	display: flex;
	flex-direction: row;
`;

const AlertContent = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`;

// d.skuza: max and min is to force 4px, sometimes browser slightly changes that width
const AlertBar = styled.div`
	width: 4px;
	min-width: 4px;
	max-width: 4px;
	border-radius: 8px;
`;

const mapThemeWithBackgroundColor = {
	light: {
		bgColor: defaultTheme.v2.color.neutral[100],
		fontColor: defaultTheme.v2.color.neutral[900],
		closeButton: defaultTheme.v2.color.black[500],
	},
	dark: {
		bgColor: defaultTheme.v2.color.neutral[1200],
		fontColor: defaultTheme.v2.color.neutral[300],
		closeButton: defaultTheme.v2.color.white[500],
	},
};

const Alert = forwardRef<HTMLDivElement, IAlertProps>(
	({ className, icon, iconSize, children, closeButton, onClose: onCloseProps }, ref) => {
		const alertContext = useAlertContext();

		const onClose = useCallback(() => {
			if (onCloseProps) {
				onCloseProps();
			} else if (alertContext) {
				alertContext.closeAlert();
			}
		}, [onCloseProps]);

		const shouldDisplayIconWrapper = icon ?? closeButton;

		return (
			<AlertWrapper className={`${className}`} ref={ref}>
				<AlertBar />
				<AlertContent>
					{shouldDisplayIconWrapper && (
						<IconWrapper>
							{icon && <Icon size={iconSize} variant={icon} />}
							{closeButton && (
								<IconButton icon="close-v3" variant="filled" onClick={onClose} />
							)}
						</IconWrapper>
					)}
					<AlertChildrenWrapper>{children}</AlertChildrenWrapper>
				</AlertContent>
			</AlertWrapper>
		);
	}
);
Alert.displayName = 'Alert';
const AlertTitleInternal = ({ children, className }: PropsWithChildren<{ className?: string }>) => {
	return (
		<Text bold className={className}>
			{children}
		</Text>
	);
};

export const AlertTitle = styled(AlertTitleInternal)``;

const AlertDescriptionInternal = ({
	children,
	className,
}: PropsWithChildren<{ className?: string }>) => {
	return <Text className={className}>{children}</Text>;
};

export const AlertDescription = styled(AlertDescriptionInternal)``;

//todo iczyryk 02.12.2022 #/MIP-245 - waiting for buttonV2
export const AlertButton = styled.div``;

const AlertChildrenWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const IconWrapper = styled.div`
	display: flex;
	justify-content: space-between;
`;

const getPropsBySize = (size: IAlertProps['size']): IAlertPropsBySize => {
	switch (size) {
		case 's':
			return {
				typographyVariants: {
					title: 'sans/14/600',
					description: 'sans/14/400',
					buttonText: 'sans/11/600',
				},
				alertIcon: '1rem',
				iconWrapper: {
					marginLeft: '0.5rem',
					marginBottom: '0.5rem',
				},
				alertButton: {
					iconSize: '0.75rem',
					marginRight: '0.25rem',
					marginTop: '0.5rem',
				},
				contentMargins: {
					childrenWrapperMarginLeft: '0.5rem',
					titleMarginBottom: '',
				},
				padding: '0.5rem',
			};
		case 'm':
			return {
				typographyVariants: {
					title: 'sans/17/600',
					description: 'sans/17/400',
					buttonText: 'sans/11/600',
				},
				alertIcon: '1.5rem',
				iconWrapper: {
					marginLeft: '1rem',
					marginBottom: '1rem',
				},
				alertButton: {
					iconSize: '0.875rem',
					marginRight: '0.5rem',
					marginTop: '1rem',
				},
				contentMargins: {
					childrenWrapperMarginLeft: '1rem',
					titleMarginBottom: '0.25rem',
				},
				padding: '1rem',
			};
		default:
			return {
				typographyVariants: {
					title: 'sans/25/600',
					description: 'sans/25/400',
					buttonText: 'sans/17/600',
				},
				alertIcon: '2rem',
				iconWrapper: {
					marginLeft: '1.15rem',
					marginBottom: '2rem',
				},
				alertButton: {
					iconSize: '1.5rem',
					marginRight: '0.5rem',
					marginTop: '1rem',
				},
				contentMargins: {
					childrenWrapperMarginLeft: '1rem',
					titleMarginBottom: '0.25rem',
				},
				padding: '1rem',
			};
	}
};

const mapStateToColor: Record<IAlertProps['state'], string> = {
	info: defaultTheme.v3.colors.blue[6],
	success: defaultTheme.v3.colors.teal[8],
	warning: defaultTheme.v2.color.warning[600],
	danger: defaultTheme.v2.color.error[600],
	infoV3: defaultTheme.v3.colors.blue[6],
	dangerV3: defaultTheme.v3.colors.red[6],
};

const Styled = styled(Alert)`
	box-shadow: 0 ${rem(2)} ${rem(4)} 0 ${({ theme }) => theme.v2.color.neutral[500]};
	border-radius: ${rem(8)};
	${({ alertTheme = 'light' }) => {
		return css`
			background-color: ${mapThemeWithBackgroundColor[alertTheme].bgColor};
			color: ${mapThemeWithBackgroundColor[alertTheme].fontColor};
			${IconButton} {
				background-color: transparent;
				padding: 0;
				color: ${mapThemeWithBackgroundColor[alertTheme].closeButton};
			}
		`;
	}};
	${AlertBar} {
		${({ state = 'info' }) => {
			return css`
				background-color: ${mapStateToColor[state]};
			`;
		}};
	}

	${IconWrapper} > ${Icon} {
		${({ state = 'info' }) => {
			return css`
				color: ${mapStateToColor[state]};
			`;
		}};
	}

	${({ size }) => {
		const alertPropsVariables = getPropsBySize(size);
		return css`
			padding: ${alertPropsVariables.padding};
			${IconWrapper} {
				margin-bottom: ${alertPropsVariables.iconWrapper.marginBottom};
				margin-left: ${alertPropsVariables.iconWrapper.marginLeft};

				${Icon}.svg {
					width: ${alertPropsVariables.alertIcon};
					height: ${alertPropsVariables.alertIcon};
				}
			}

			${AlertChildrenWrapper} {
				margin-left: ${alertPropsVariables.contentMargins.childrenWrapperMarginLeft};

				${AlertTitle} {
					${typographyCss({
						variant: alertPropsVariables.typographyVariants.title,
					})};
					margin-bottom: ${alertPropsVariables.contentMargins.titleMarginBottom};
				}

				${AlertDescription} {
					${typographyCss({
						variant: alertPropsVariables.typographyVariants.description,
					})}
				}
			}

			${AlertButton} {
				${Button} {
					${Icon} {
						width: ${alertPropsVariables.alertButton.iconSize};
						height: ${alertPropsVariables.alertButton.iconSize};
						margin-right: ${alertPropsVariables.alertButton.marginRight};
						margin-top: ${alertPropsVariables.alertButton.marginTop};
					}

					${Text} {
						${typographyCss({
							variant: alertPropsVariables.typographyVariants.buttonText,
						})}
					}
				}
			}
		`;
	}};
`;

export { Styled as Alert };
